/**
 * Katalog aller Objekttypen, die im Audit-Log auftauchen können — mit deutschem
 * Klarnamen und Modul-Zuordnung.
 *
 * Warum geteilt (packages/shared)? Beide Seiten brauchen dieselbe Wahrheit:
 * - die API löst `entityType` beim Ausliefern in Klarnamen auf,
 * - das Frontend baut daraus die Filter-Dropdowns („Modul", „Objekttyp").
 *
 * Der technische Schlüssel ist bewusst der Prisma-Modellname, den
 * `AuditService.record()` schreibt — dadurch ist der Katalog beim Anlegen einer
 * neuen Entität leicht zu ergänzen. Fehlt ein Typ hier, fällt die Anzeige auf
 * den Rohwert zurück (kein Fehler, aber bitte ergänzen).
 */

export const AUDIT_MODULE_KEYS = [
  'sales',
  'crm',
  'projects',
  'procurement',
  'inventory',
  'logistics',
  'production',
  'engineering',
  'assembly',
  'hr',
  'accounting',
  'qm',
  'dms',
  'feedback',
  'admin',
] as const;

export type AuditModuleKey = (typeof AUDIT_MODULE_KEYS)[number];

export const AUDIT_MODULE_LABELS: Record<AuditModuleKey, string> = {
  sales: 'Verkauf',
  crm: 'CRM',
  projects: 'Projekte',
  procurement: 'Beschaffung',
  inventory: 'Artikel & Lager',
  logistics: 'Logistik',
  production: 'Produktion',
  engineering: 'Engineering',
  assembly: 'Montage',
  hr: 'Personal & Zeit',
  accounting: 'Buchhaltung',
  qm: 'Qualität',
  dms: 'Dateien',
  feedback: 'Verbesserungen',
  admin: 'Administration',
};

export type AuditEntityCatalogEntry = {
  /** Deutscher Klarname im Singular, z. B. „Verkaufsbeleg". */
  label: string;
  module: AuditModuleKey;
};

/** entityType (Prisma-Modellname) → Klarname + Modul. */
export const AUDIT_ENTITY_CATALOG: Record<string, AuditEntityCatalogEntry> = {
  // Verkauf
  SalesDocument: { label: 'Verkaufsbeleg', module: 'sales' },
  SalesPipelineEntry: { label: 'Pipeline-Eintrag', module: 'sales' },
  PaymentSchedule: { label: 'Zahlungsplan', module: 'sales' },
  PaymentScheduleItem: { label: 'Zahlungsplan-Position', module: 'sales' },

  // CRM
  Customer: { label: 'Kunde/Lieferant', module: 'crm' },
  Contact: { label: 'Ansprechpartner', module: 'crm' },
  CrmActivity: { label: 'CRM-Aktivität', module: 'crm' },
  DeliveryAddress: { label: 'Lieferadresse', module: 'crm' },

  // Projekte
  Project: { label: 'Projekt', module: 'projects' },
  ProjectPhase: { label: 'Projektphase', module: 'projects' },
  ProjectMilestone: { label: 'Meilenstein', module: 'projects' },
  ProjectTask: { label: 'Projektaufgabe', module: 'projects' },
  ProjectNote: { label: 'Projektnotiz', module: 'projects' },
  ProjectChecklistItem: { label: 'Checklisten-Punkt', module: 'projects' },
  ProjectMaterialList: { label: 'Materialliste', module: 'projects' },
  ProjectMaterialItem: { label: 'Materialposition', module: 'projects' },
  ProjectCalculation: { label: 'Projektkalkulation', module: 'projects' },
  ProjectPlan: { label: 'Projektplanung', module: 'projects' },
  ProjectProtocol: { label: 'Projektprotokoll', module: 'projects' },

  // Beschaffung
  PurchaseOrder: { label: 'Bestellung', module: 'procurement' },
  GoodsReceipt: { label: 'Wareneingang', module: 'procurement' },
  Forecast: { label: 'Bedarfsvorschau', module: 'procurement' },

  // Artikel & Lager
  Article: { label: 'Artikel', module: 'inventory' },
  ArticleDrawing: { label: 'Artikel-Zeichnung', module: 'inventory' },
  ArticlePriceTier: { label: 'Preisstaffel', module: 'inventory' },
  StockMovement: { label: 'Lagerbewegung', module: 'inventory' },
  StockReservation: { label: 'Reservierung', module: 'inventory' },
  BlockedStockItem: { label: 'Sperrlager-Position', module: 'inventory' },
  InventoryCount: { label: 'Inventur', module: 'inventory' },
  CommodityMainGroup: { label: 'Hauptwarengruppe', module: 'inventory' },
  SelectionPool: { label: 'Selektionspool', module: 'inventory' },

  // Logistik
  PickingOrder: { label: 'Kommissionierauftrag', module: 'logistics' },
  Transport: { label: 'Transport', module: 'logistics' },

  // Produktion
  ProductionOrder: { label: 'Fertigungsauftrag', module: 'production' },
  BomItem: { label: 'Stücklisten-Position', module: 'production' },

  // Engineering
  ArticleNumberRequest: { label: 'Artikelnummern-Antrag', module: 'engineering' },
  DrawingNumber: { label: 'Zeichnungsnummer', module: 'engineering' },
  CommodityGroup: { label: 'Warengruppe', module: 'engineering' },
  Material: { label: 'Materialcode', module: 'engineering' },
  TaricCode: { label: 'Zolltarifnummer', module: 'engineering' },

  // Montage
  AssemblyTask: { label: 'Montage-Einsatz', module: 'assembly' },
  AssemblyReport: { label: 'Montagebericht', module: 'assembly' },
  AssemblyDayLog: { label: 'Bautagebuch-Eintrag', module: 'assembly' },
  MaintenanceSchedule: { label: 'Wartungsplan', module: 'assembly' },
  CalendarFeedToken: { label: 'Kalender-Abo', module: 'assembly' },

  // Personal & Zeit
  Employee: { label: 'Mitarbeiter', module: 'hr' },
  Position: { label: 'Stelle', module: 'hr' },
  TimeEntry: { label: 'Zeitbuchung', module: 'hr' },
  LeaveRequest: { label: 'Abwesenheitsantrag', module: 'hr' },
  Trip: { label: 'Dienstreise', module: 'hr' },
  TtProject: { label: 'Zeiterfassungs-Projekt', module: 'hr' },
  TtCategory: { label: 'Zeiterfassungs-Kategorie', module: 'hr' },
  TtSubCategory: { label: 'Zeiterfassungs-Unterkategorie', module: 'hr' },

  // Buchhaltung
  CashBookMonth: { label: 'Kassenbuch-Monat', module: 'accounting' },
  CashBookEntry: { label: 'Kassenbuch-Buchung', module: 'accounting' },
  Payment: { label: 'Zahlung', module: 'accounting' },

  // Qualität
  QmProcess: { label: 'QM-Prozess', module: 'qm' },
  QmTimeEntry: { label: 'QM-Zeitaufwand', module: 'qm' },
  QmCostEntry: { label: 'QM-Kosten', module: 'qm' },

  // Dateien
  DmsFolder: { label: 'Ordner', module: 'dms' },
  DmsDocument: { label: 'Dokument', module: 'dms' },

  // Verbesserungen
  FeedbackItem: { label: 'Meldung', module: 'feedback' },
  FeedbackComment: { label: 'Meldungs-Kommentar', module: 'feedback' },
  FeedbackAttachment: { label: 'Meldungs-Anhang', module: 'feedback' },

  // Administration
  User: { label: 'Benutzer', module: 'admin' },
  Role: { label: 'Rolle', module: 'admin' },
  RoleChangeRequest: { label: 'Rechte-Antrag', module: 'admin' },
  CompanySettings: { label: 'Firmen-Einstellungen', module: 'admin' },
  LegalEntity: { label: 'Firma', module: 'admin' },
  NumberSequence: { label: 'Nummernkreis', module: 'admin' },
  PaymentTermsTemplate: { label: 'Zahlungsbedingung', module: 'admin' },
  TaxExemptReasonTemplate: { label: 'Steuerbefreiungs-Vorlage', module: 'admin' },
  PaymentScheduleTemplate: { label: 'Zahlungsplan-Vorlage', module: 'admin' },
  ImportJob: { label: 'Datenimport', module: 'admin' },
  ReportRun: { label: 'Report-Lauf', module: 'admin' },
  System: { label: 'System', module: 'admin' },
};

/** Deutscher Klarname eines Objekttyps; unbekannte Typen bleiben unverändert stehen. */
export function auditEntityTypeLabel(entityType: string): string {
  return AUDIT_ENTITY_CATALOG[entityType]?.label ?? entityType;
}

/** Modul eines Objekttyps; unbekannte Typen landen in „Administration". */
export function auditEntityModule(entityType: string): AuditModuleKey {
  return AUDIT_ENTITY_CATALOG[entityType]?.module ?? 'admin';
}

/** Alle Objekttypen eines Moduls (für den Modul-Filter). */
export function auditEntityTypesOfModule(module: AuditModuleKey): string[] {
  return Object.entries(AUDIT_ENTITY_CATALOG)
    .filter(([, entry]) => entry.module === module)
    .map(([type]) => type);
}

/**
 * Objekttypen, deren Klarname den Suchbegriff enthält. Damit findet die
 * Freitextsuche „Bestellung" auch Einträge mit `entityType = PurchaseOrder`.
 */
export function auditEntityTypesMatching(term: string): string[] {
  const needle = term.trim().toLowerCase();
  if (!needle) return [];
  return Object.entries(AUDIT_ENTITY_CATALOG)
    .filter(([, entry]) => entry.label.toLowerCase().includes(needle))
    .map(([type]) => type);
}
